import * as React from 'react';

import { useToast } from '@rematter/ui-web/hooks';
import { Copy } from 'lucide-react';

import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type Props = {
  label: string;
  text?: string | null;
  formattedText?: string;
  icon?: React.ReactNode;
  allowCopy?: boolean;
  truncate?: boolean;
  wrap?: boolean;
};

export const LabeledText: React.FC<Props> = ({
  label,
  formattedText,
  text,
  icon,
  allowCopy,
  truncate = false,
  wrap = false,
}) => {
  const t = useTranslations();
  const toast = useToast();
  const textRef = React.useRef<HTMLDivElement>(null);
  const [isTruncated, setIsTruncated] = React.useState(false);

  const onCopy = () => {
    if (text) {
      navigator.clipboard.writeText(text);
      toast.success(t.shared.copiedToClipboard);
    }
  };

  const displayText = text ? (formattedText ?? text) : '-';

  const textClasses = [
    'text-base',
    truncate && 'truncate',
    wrap && 'break-words',
    !truncate && !wrap && 'truncate', // Default to truncate if neither option is specified
  ]
    .filter(Boolean)
    .join(' ');

  // Check if text is actually truncated
  React.useEffect(() => {
    const element = textRef.current;
    if (element && (truncate || (!truncate && !wrap))) {
      setIsTruncated(element.scrollWidth > element.clientWidth);
    } else {
      setIsTruncated(false);
    }
  }, [displayText, truncate, wrap]);

  return (
    <div className="@LabeledText flex flex-col gap-1">
      <div className="text-sm font-semibold text-neutral/60">{label}</div>
      <div className="flex flex-row items-center gap-2">
        {icon}
        <div
          ref={textRef}
          className={textClasses}
          title={isTruncated ? displayText : undefined}
        >
          {displayText}
        </div>
        {allowCopy && (
          <Copy
            className="flex-shrink-0 text-neutral/60 hover:cursor-pointer hover:text-neutral/80"
            size={16}
            onClick={onCopy}
          />
        )}
      </div>
    </div>
  );
};
